import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import {
	AuthData,
	UI_HIDE_INACTIVE_ENTITIES_ENABLED,
} from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import {
	EXPOSURE_CLONE_FAILURE,
	EXPOSURE_CLONE_LABEL,
	EXPOSURE_CLONE_SUCCESS,
} from '../../../data/labelsAndMessages';
import { Exposure, ExposureCharacteristics } from '../../../types/policy';
import { tableStyles } from '../../../util/styles';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../../util/tableUtil';
import {
	getActiveCharacteristics,
	getEntityNameFrom_DescriptionField,
	isExposureActive,
	isPolicyEditable,
	sortCharacteristicsByStartDate,
} from '../../../util/util';
import { StyledEmptyMessage, StyledTableActions } from '../../common';
import SortableTable from '../../SortableTable';
import { errorToast, successToast } from '../../Toast';

interface Props {
	exposures: Exposure[];
	authData: AuthData;
	policyData: PolicyData;
	openConfirmationModal: (exposure: Exposure) => void;
}

interface Data {
	exposureId: string;
	exposureName: string;
	dateCreated: string;
	isActive: boolean;
	actions: any;
	link: string;
}

function createData(
	exposureId: string,
	exposureName: string,
	dateCreated: string,
	isActive: boolean,
	actions: any,
	link: string,
): Data {
	return { exposureId, exposureName, dateCreated, isActive, actions, link };
}

const ExposuresList = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const { exposures, policyData, authData, openConfirmationModal } = props;
	const hideInactiveEntities =
		authData?.auth?.configProperties?.[UI_HIDE_INACTIVE_ENTITIES_ENABLED];
	const {
		policyUpdateObj,
		quoteUpdateObj,
		selectedQuote,
		productConfig,
		policy,
	} = policyData;
	const updateObject = selectedQuote ? quoteUpdateObj : policyUpdateObj;
	const currentExposures = exposures as any;
	const addedExposures = updateObject.addExposures || [];
	const removedExposures = updateObject.removeExposures || [];
	const combinedExposures = currentExposures.concat(addedExposures);
	const filteredExposures = combinedExposures.filter(
		(e: any) => !removedExposures.includes(e.locator),
	);
	const isInEditMode =
		authData.hasPermission('quote.update') &&
		isPolicyEditable(policy, selectedQuote);

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('dateCreated');

	const emptyTable = !filteredExposures.length ? (
		<StyledEmptyMessage>No {t('exposure.plural.upper')}</StyledEmptyMessage>
	) : null;
	const classes = tableStyles();

	const headCells: HeadCell[] = [
		{
			id: 'exposureName',
			rightAlign: false,
			label: `${t('exposure.singular.upper')} Name`,
		},
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
		{ id: 'Status', rightAlign: false, label: 'Status' },
	];

	if (isInEditMode) {
		headCells.push({ id: 'actions', rightAlign: false, label: 'Actions' });
	}

	const rows = filteredExposures.map((exposure: any, index: number) => {
		const latestActiveCharacteristic = sortCharacteristicsByStartDate(
			getActiveCharacteristics(
				exposure.characteristics,
			) as ExposureCharacteristics[],
		).pop();
		const nameFromDescriptionField = getEntityNameFrom_DescriptionField(
			latestActiveCharacteristic,
		);
		const exposureConfig = productConfig.policyConfiguration?.exposures.find(
			(e) => e.name === exposure.name,
		);
		const exposureDisplayName = exposureConfig.displayName
			? exposureConfig.displayName
			: exposure.name;
		const exposureName = nameFromDescriptionField
			? nameFromDescriptionField
			: exposureDisplayName
			? exposureDisplayName
			: exposure.exposureName;
		const urlId = exposure.displayId
			? exposure.displayId
			: exposure.locator || '';
		const displayId = exposure.displayId ? exposure.displayId : 'N/A';
		const createdTimestamp = exposure.createdTimestamp
			? exposure.createdTimestamp
			: Date.now();
		const exposureDetailsLink =
			!selectedQuote || policy.issuedTimestamp
				? `exposures/${urlId}`
				: `quotes/${selectedQuote.locator}/exposures/${urlId}`;
		const isActive = isExposureActive(
			exposure,
			policy,
			policyData.policyViewTimestamp,
		);
		const removeOnClick = (e: any) => {
			e.preventDefault();
			e.stopPropagation();
			openConfirmationModal(exposure);
		};
		const cloneOnClick = async (e: any) => {
			e.preventDefault();
			e.stopPropagation();
			try {
				policyData.setLoading(true);
				await policyData.cloneExposure(
					exposure,
					getValues(),
					reset,
					dirtyFields,
				);
				successToast(EXPOSURE_CLONE_SUCCESS());
			} catch (e) {
				errorToast(EXPOSURE_CLONE_FAILURE());
				if (await handleUnauthorizedResponse(e, authData)) {
					return;
				}
			} finally {
				policyData.setLoading(false);
			}
		};

		const actions = (
			<StyledTableActions>
				{isInEditMode && <a onClick={removeOnClick}>Remove</a>}
				{isInEditMode && <a onClick={cloneOnClick}>{EXPOSURE_CLONE_LABEL()}</a>}
			</StyledTableActions>
		);
		return createData(
			displayId,
			exposureName,
			createdTimestamp,
			isActive,
			actions,
			`/policy/${policy.locator}/${exposureDetailsLink}`,
		);
	});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			if (!row.isActive && hideInactiveEntities) {
				return null;
			}
			return (
				<TableRow
					hover
					tabIndex={-1}
					key={row.exposureId}
					component={Link}
					to={row.link}
					className={classes.tableRow}
				>
					<TableCell>{row.exposureName}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					<TableCell>{row.isActive ? 'Active' : 'Inactive'}</TableCell>
					{isInEditMode && <TableCell>{row.actions}</TableCell>}
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withPolicyContext<Props>(ExposuresList);
